/**
 * Client ID System for Request-Response Correlation
 *
 * This system implements a mechanism to prevent duplicate processing of websocket messages
 * that were triggered by our own requests. Here's how it works:
 *
 * 1. Request Flow:
 *    - When making an API request from the frontend, we generate a unique clientId
 *    - This clientId is stored in sessionStorage (scoped to the current tab)
 *    - The clientId is sent as a header with the API request
 *
 * 2. Backend Processing:
 *    - When the backend processes the request, it includes the clientId in any
 *      websocket messages it broadcasts
 *    - This allows us to identify which messages were triggered by our own requests
 *
 * 3. Message Processing:
 *    - When receiving a websocket message, we check if:
 *      a) The message's clientId matches our stored clientId
 *      b) The message type is in our skip list (events we don't want to process)
 *    - If both conditions are true, we skip processing the message
 *    - This prevents duplicate processing of events we triggered ourselves
 *
 * Example:
 * 1. Frontend makes a POST request to create a resource
 * 2. Request includes clientId header: "123_abc"
 * 3. Backend processes request and broadcasts websocket message
 * 4. Message includes clientId: "123_abc"
 * 5. Frontend receives message, sees matching clientId
 * 6. Frontend skips processing since it was our own request
 *
 * This is particularly useful for:
 * - Preventing UI updates for actions we just performed
 * - Reducing unnecessary state updates
 */
var PRO_CLIENT_ID_KEY = 'pro_client_id';
var PATIENT_CLIENT_ID_KEY = 'patient_client_id';
/**
 * Safely parses and validates the clientId for a given key.
 * Returns the clientId only if it matches the expected format, otherwise returns null.
 */

export var isClientIdSafe = function isClientIdSafe(clientId) {
  // Pattern: digits + underscore + 13 base36 chars
  var pattern = /^\d+_[a-z0-9]{13}$/;
  return typeof clientId === 'string' && pattern.test(clientId);
};
/**
 * Generates a unique clientId if one doesn't exist
 * Returns the existing clientId if one is already stored
 *
 * The clientId is used to correlate websocket messages with our own requests
 * to prevent duplicate processing of events we triggered.
 */

export var getOrCreateClientId = function getOrCreateClientId(key) {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    // Return a temporary ID for server-side rendering
    return "server_".concat(Date.now());
  }

  var requestId = window[key];

  if (!requestId || !isClientIdSafe(requestId)) {
    // Generate a unique ID using timestamp and random string
    requestId = "".concat(Date.now(), "_").concat(Math.random().toString(36).substring(2, 15));
    window[key] = requestId;
  }

  return requestId;
};
/**
 * Gets the current clientId if it exists
 * Returns null if no clientId is stored
 *
 * Used to check if an incoming websocket message was triggered by our own request
 */

export var getClientId = function getClientId(key) {
  if (typeof window === 'undefined') {
    return null;
  }

  var clientId = window[key];
  return isClientIdSafe(clientId) ? clientId : null;
};
/**
 * Clears the stored clientId
 *
 * Useful when logging out or when you want to force a new clientId
 */

export var clearClientId = function clearClientId(key) {
  if (typeof window === 'undefined') {
    return;
  }

  delete window[key];
};
/**
 * PRO-specific client ID functions
 * Used in the professional interface of the application
 */

export var getOrCreateProClientId = function getOrCreateProClientId() {
  return getOrCreateClientId(PRO_CLIENT_ID_KEY);
};
export var getProClientId = function getProClientId() {
  return getClientId(PRO_CLIENT_ID_KEY);
};
export var clearProClientId = function clearProClientId() {
  clearClientId(PRO_CLIENT_ID_KEY);
};
/**
 * PATIENT-specific client ID functions
 * Used in the patient interface of the application
 */

export var getOrCreatePatientClientId = function getOrCreatePatientClientId() {
  return getOrCreateClientId(PATIENT_CLIENT_ID_KEY);
};
export var getPatientClientId = function getPatientClientId() {
  return getClientId(PATIENT_CLIENT_ID_KEY);
};
export var clearPatientClientId = function clearPatientClientId() {
  clearClientId(PATIENT_CLIENT_ID_KEY);
};
//# sourceMappingURL=client-request-id.js.map